import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';
import { PaginatedState, PaginatedStateLoadingMode, PaginatedStateModel } from '@shared/ui';

export interface WorkspaceBase {
  id: string;
  name: string;
}

export interface WorkspaceData extends WorkspaceBase {
  organizationId: string;
  caseTypeId: string;
  createdAt: Date;
  usedStorage: number;
  numMembers?: number;
  numCases?: number;
}

export interface WorkspacesStateModel extends PaginatedStateModel<WorkspaceData> {
  organizationId: string | undefined;
  caseTypeFilter: string | undefined;
}

export const WORKSPACES_STATE_LOADING_MODE = new InjectionToken<PaginatedStateLoadingMode>(
  'WORKSPACES_STATE_LOADING_MODE'
);

@Injectable()
export class WorkspacesState extends PaginatedState<WorkspaceData, WorkspacesStateModel> {
  constructor(@Optional() @Inject(WORKSPACES_STATE_LOADING_MODE) loadingMode?: PaginatedStateLoadingMode) {
    super(loadingMode ? { loadingMode } : undefined, { organizationId: undefined, caseTypeFilter: undefined });
  }

  // ============================================================================
  // Custom Selectors
  // ============================================================================

  public readonly organizationId$ = this.select((state) => state.organizationId);
  public readonly caseTypeFilter$ = this.select((state) => state.caseTypeFilter);

  public readonly workspaces$ = this.items$;
  public readonly totalWorkspaces$ = this.totalCount$;

  // ============================================================================
  // Custom Updaters
  // ============================================================================

  public readonly setOrganizationId = this.updater(
    (state, organizationId: string | undefined) =>
      ({
        ...state,
        organizationId,
        items: this.itemsAdapter.removeAll(state.items),
        hasMoreItems: true,
        previousPageTokens: [],
        nextToken: undefined,
        loading: true,
      } as WorkspacesStateModel)
  );

  public readonly setCaseTypeFilter = this.updater(
    (state, caseTypeFilter: string | undefined) =>
      ({
        ...state,
        caseTypeFilter,
        items: this.itemsAdapter.removeAll(state.items),
        hasMoreItems: true,
        previousPageTokens: [],
        nextToken: undefined,
        searching: true,
      } as WorkspacesStateModel)
  );
}
